import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { customersAPI } from '../services/api';
import { FiArrowLeft, FiEdit, FiTrash2, FiPlus, FiMaximize2, FiShoppingBag } from 'react-icons/fi';
import { format } from 'date-fns';

const CustomerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await customersAPI.getDetails(id);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${data.customer.name}? This will also delete all associated measurements and orders.`)) {
      try {
        await customersAPI.delete(id);
        navigate('/customers');
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert('Error deleting customer. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Customer not found</p>
        <Link to="/customers" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
          Back to Customers
        </Link>
      </div>
    );
  }

  const { customer, measurements, orders } = data;

  return (
    <div>
      <button
        onClick={() => navigate('/customers')}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6"
      >
        <FiArrowLeft size={20} />
        <span>Back to Customers</span>
      </button>

      {/* Customer Info */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{customer.name}</h1>
            {customer.phone && (
              <p className="text-gray-600 mt-2">📞 {customer.phone}</p>
            )}
            {customer.email && (
              <p className="text-gray-600">✉️ {customer.email}</p>
            )}
            {customer.address && (
              <p className="text-gray-600 mt-2">📍 {customer.address}</p>
            )}
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/customers/${id}/edit`}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
            >
              <FiEdit size={18} />
              <span>Edit</span>
            </Link>
            <button
              onClick={handleDelete}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <FiTrash2 size={18} />
              <span>Delete</span>
            </button>
          </div>
        </div>
        {customer.notes && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">{customer.notes}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Measurements */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <FiMaximize2 size={24} />
              <span>Measurements ({measurements.length})</span>
            </h2>
            <Link
              to={`/measurements/new?customer_id=${id}`}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <FiPlus size={18} />
              <span>Add</span>
            </Link>
          </div>
          {measurements.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No measurements yet</p>
          ) : (
            <div className="space-y-3">
              {measurements.map((measurement) => (
                <Link
                  key={measurement.id}
                  to={`/measurements/${measurement.id}/edit`}
                  className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-800">{measurement.garment_type}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {format(new Date(measurement.measurement_date), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <span className="text-primary-600 text-sm">View →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Orders */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <FiShoppingBag size={24} />
              <span>Orders ({orders.length})</span>
            </h2>
            <Link
              to={`/orders/new?customer_id=${id}`}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
            >
              <FiPlus size={18} />
              <span>Add</span>
            </Link>
          </div>
          {orders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No orders yet</p>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <Link
                  key={order.id}
                  to={`/orders/${order.id}/edit`}
                  className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-800">{order.garment_type}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {format(new Date(order.order_date), 'MMM dd, yyyy')}
                      </p>
                      <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
                        order.status === 'completed' ? 'bg-green-100 text-green-800' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                    {order.price && (
                      <span className="text-primary-600 font-semibold">PKR {order.price}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;

